/** @format */

import mapboxgl from "mapbox-gl";
import React, { type FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import "mapbox-gl/dist/mapbox-gl.css";

import { useTranslation } from "react-i18next";
import { delay, parseGeoData } from "senf-shared";
import { Dot } from "../../../assets/icons";
import IdeaPin from "../../../assets/icons/IdeaPin";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Dropdown from "../../core/dropdown/Dropdown";
import Icon from "../../core/icon/Icon";
import InfoToast from "../../molecules/infoToast/InfoToast";
import use3d from "./hooks/use3d";
import useClickMarkers from "./hooks/useClickMarkers";
import { useGisData } from "./hooks/useGisData";
import useHeatMap from "./hooks/useHeatMap";
import useHover from "./hooks/useHover";
import { useIcons } from "./hooks/useIcons";
import useIdeaPin from "./hooks/useIdeaPin";
import { useLabels } from "./hooks/useLabels";
import useMergedLegend from "./hooks/useMergedLegend";
import usePolygonMask from "./hooks/usePolygonMask";
import { MapContainer } from "./Map.styles";
import type { MapProps } from "./Map.types";
import { MapAccessibility } from "./MapAccessibility";
import { addImagesToMap } from "./utils/addImagesToMap";
import { cleanFeatureCollection } from "./utils/cleanFeatureCollection";
import { normalizeGisData } from "./utils/normalizeGisData";
import { showMapbounds } from "./utils/showMapbounds";

// RTL text plugin setup for Mapbox GL v3
if (mapboxgl.getRTLTextPluginStatus() === "unavailable") {
  mapboxgl.setRTLTextPlugin(
    "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js",
    null as any,
    true,
  );
}

const MarkerPin = styled.div`
  transform: translateY(-100%);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: none;
  animation: showPin 0.4s ease-in-out;

  @keyframes showPin {
    0% {
      opacity: 0;
    }
    50%{
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const MapboxMap: FC<MapProps> = ({
  id = "map-normal",
  variant,
  children,
  project,
  mapStyle = null,
  navigation = true,
  initialMapViewport = {
    latitude: 51.1657,
    longitude: 10.4515,
    zoom: 5,
    duration: 0,
    pitch: 0,
    bearing: 50,
    maxZoom: 20,
    minZoom: 5,
  },
  mapbounds,
  flyToMapbounds = true,
  pinData,
  handleClickIdeaMarker,
  statefulMap,
  setStatefulMap,
  optimizeString,
  paddings,
  gisData,
  legendData,
  resize,
  initialAnimationDuration,
  heatmapStyle,
  preserveDrawingBuffer,
  modelObj,
  accessibilityInfotext,
  onCameraMove,
  enableCameraBoundsRestriction = true,
}) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const ideaPinRef = useRef(null);
  const hover = useHover(project);
  const clickMarkers = useClickMarkers();
  const [setPolygonMaskLayer, setPolygonMaskData] = usePolygonMask();
  const legend = useMergedLegend(gisData || legendData);
  const [setLabelsLayer, setLabelsData] = useLabels();
  const [setIconsLayer, setIconsData] = useIcons();
  const [setGisLayer, setGisData] = useGisData();
  const [ideaMarkerColor, setIdeaMarkerColor] = useState<string | null>(null);

  useEffect(() => {
    if (!statefulMap?._loaded) return;
    if (!gisData) {
      setGisData(null);
      setLabelsData(null);
      setIconsData(null);
      return;
    }
    clickMarkers(statefulMap, handleClickIdeaMarker);
    if (!isMobile) {
      hover(statefulMap);
    }
    const normalizedGisData = normalizeGisData(gisData);
    setTimeout(() => {
      let finalLabelData = normalizedGisData;

      if (heatmapStyle?.mapStyle === "drawn") {
        const labelData = JSON.parse(JSON.stringify(normalizedGisData));

        // Now, when you modify labelData, normalizedGisData will remain unchanged
        labelData?.[0].data?.features.push(
          ...gisData[gisData?.length - 1].features,
        );

        finalLabelData = labelData;
      }

      setLabelsData(finalLabelData);
      setIconsData(finalLabelData);
      setGisData(normalizedGisData); // Assuming you still want to set this for some reason
    }, 100);
  }, [gisData, statefulMap?._loaded, heatmapStyle?.mapStyle]);

  if (heatmapStyle && gisData?.length > 0) {
    useHeatMap({
      map: statefulMap,
      geoData: cleanFeatureCollection(gisData[gisData?.length - 1]),
      type: heatmapStyle.mapStyle,
      mapbounds: parseGeoData(mapbounds),
      heatmapStyle,
      setLabelsData,
    });
  }

  if (modelObj) {
    use3d(statefulMap, modelObj);
  }

  // Initialize map when component mounts
  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      // id: Math.random() * 1000,
      // style: "mapbox://styles/tmorino/ckclpzylp0vgp1iqsrp4asxt6?optimize=true",
      style:
        mapStyle ||
        (optimizeString
          ? "mapbox://styles/tmorino/claqq4d85005014ohjoqds4wt?optimize=true"
          : "mapbox://styles/tmorino/claqq4d85005014ohjoqds4wt"),
      center: [initialMapViewport.longitude, initialMapViewport.latitude],
      zoom: initialMapViewport.zoom,
      pitch: initialMapViewport.pitch,
      maxZoom: initialMapViewport.maxZoom,
      minZoom: initialMapViewport.minZoom,
      preserveDrawingBuffer: preserveDrawingBuffer || false,
      attributionControl: true,
    });

    const setTabindex = () => {
      const mapboxControlElements = document.querySelectorAll(
        ".mapboxgl-ctrl, .mapboxgl-ctrl-logo, .mapboxgl-ctrl-group *, .mapboxgl-ctrl-attrib, .mapboxgl-ctrl-attrib-button, .mapboxgl-ctrl-attrib-inner *",
      );
      mapboxControlElements.forEach((element) => {
        element.setAttribute("tabindex", "-1");
      });
    };

    map.on("load", () => {
      map.getContainer().setAttribute("data-test-loaded", "true");
      setTabindex();
      // Use MutationObserver to apply tabindex to new elements
      const observer = new MutationObserver(() => {
        setTabindex();
      });

      observer.observe(map.getContainer(), {
        childList: true,
        subtree: true,
      });
    });

    setStatefulMap?.(map);

    addImagesToMap(map);
    setLabelsLayer(map);
    setIconsLayer(map);
    setGisLayer(map);
    setPolygonMaskLayer(map);

    if (navigation) {
      map.addControl(new mapboxgl.NavigationControl(), "bottom-right");
    }

    // Setup camera bounds restriction when map is ready
    map.on("load", () => {
      setupMapboxBoundsRestriction(map, mapbounds);
    });

    return () => {
      if (map) {
        map.remove();
      }
      setStatefulMap?.(null);
    };
  }, [mapStyle]);

  // Function to setup bounds restriction for Mapbox
  const setupMapboxBoundsRestriction = (map: mapboxgl.Map, mapbounds: any) => {
    if (!mapbounds) return;

    try {
      const geoData = parseGeoData(mapbounds);
      if (!geoData || !geoData.features || geoData.features.length === 0)
        return;

      // Calculate bounds from the mapbounds geometry
      let minLng = Number.POSITIVE_INFINITY;
      let minLat = Number.POSITIVE_INFINITY;
      let maxLng = Number.NEGATIVE_INFINITY;
      let maxLat = Number.NEGATIVE_INFINITY;

      geoData.features.forEach((feature: any) => {
        if (feature.geometry && feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates;

          // Handle different geometry types
          const flattenCoords = (coordArray: any[]): number[][] => {
            if (
              Array.isArray(coordArray[0]) &&
              Array.isArray(coordArray[0][0])
            ) {
              return coordArray.flat();
            }
            return coordArray;
          };

          const allCoords = flattenCoords(coords);
          allCoords.forEach((coord: number[]) => {
            if (coord.length >= 2) {
              const [lng, lat] = coord;
              minLng = Math.min(minLng, lng);
              minLat = Math.min(minLat, lat);
              maxLng = Math.max(maxLng, lng);
              maxLat = Math.max(maxLat, lat);
            }
          });
        }
      });

      // Add padding to bounds with min 500m and max 5km constraints
      // Approximate conversion: 1 degree ≈ 111km at equator
      const minPaddingKm = 150;
      const maxPaddingKm = 250;
      const minPaddingDeg = minPaddingKm / 111;
      const maxPaddingDeg = maxPaddingKm / 111;

      const calculatedLngPadding = (maxLng - minLng) * 1;
      const calculatedLatPadding = (maxLat - minLat) * 1;

      const lngPadding = Math.min(
        Math.max(calculatedLngPadding, minPaddingDeg),
        maxPaddingDeg,
      );
      const latPadding = Math.min(
        Math.max(calculatedLatPadding, minPaddingDeg),
        maxPaddingDeg,
      );

      const restrictedBounds = new mapboxgl.LngLatBounds([
        [minLng - lngPadding, minLat - latPadding], // Southwest
        [maxLng + lngPadding, maxLat + latPadding], // Northeast
      ]);

      // Set the max bounds for the map
      map.setMaxBounds(restrictedBounds);
    } catch (error) {
      console.warn("Failed to setup map bounds restriction:", error);
    }
  };

  const resizeMap = () => {
    if (statefulMap) {
      statefulMap.resize();
      showMapbounds(statefulMap, mapbounds, flyToMapbounds, setPolygonMaskData);
    }
  };
  useEffect(() => {
    setTimeout(resizeMap, 200);
    setTimeout(resizeMap, 500);
  }, [statefulMap, resize, mapbounds]);

  useEffect(() => {
    if (statefulMap && mapbounds) {
      showMapbounds(
        statefulMap,
        mapbounds,
        flyToMapbounds,
        setPolygonMaskData,
        initialAnimationDuration,
      );
      // Update bounds restriction when mapbounds change

      if (enableCameraBoundsRestriction) {
        setupMapboxBoundsRestriction(statefulMap, mapbounds);
      }
    } else if (statefulMap && !mapbounds) {
      showMapbounds(
        statefulMap,
        null,
        flyToMapbounds,
        setPolygonMaskData,
        initialAnimationDuration,
      );
      // Remove bounds restriction when no mapbounds
      statefulMap.setMaxBounds(null);
    }
  }, [statefulMap, mapbounds]);

  useEffect(() => {
    if (onCameraMove) statefulMap?.on("moveend", onCameraMove);
    return () => {
      if (onCameraMove) statefulMap?.off("moveend", onCameraMove);
    };
  }, [statefulMap, onCameraMove]);

  // SHOW DOTS (IDEAS)
  // useEffect(() => {
  //   if (statefulMap) {
  //     if (ideasData) {
  //       clickMarkers(statefulMap, handleClickIdeaMarker);
  //       if (!isMobile) {
  //         hover(statefulMap);
  //       }
  //       convertIdeasToGeoJson(ideasData).then((ideasGeoJson) =>
  //         setIdeasMarkersData(ideasGeoJson)
  //       );
  //     } else {
  //       setIdeasMarkersData(null);
  //     }
  //   }
  // }, [statefulMap, ideasData]);

  // SHOW PIN (OF IDEA)
  useEffect(() => {
    if (!statefulMap?._loaded) return;

    setTimeout(() => {
      const normalizedGisData = normalizeGisData(gisData);

      useIdeaPin(
        statefulMap,
        ideaPinRef.current,
        pinData,
        setIdeaMarkerColor,
        paddings,
        setGisData,
        normalizedGisData,
      );
    }, 200);
  }, [
    statefulMap?._loaded,
    pinData?.geoData,
    pinData?.long,
    pinData?.lat,
    isMobile,
    paddings?.bottom,
    paddings?.left,
    ideaPinRef,
    setIdeaMarkerColor,
  ]);

  return (
    <div className="relative h-full w-full" data-map-container>
      <MapAccessibility
        statefulMap={statefulMap}
        accessibilityInfotext={accessibilityInfotext}
        isMobile={isMobile}
      />

      <MapContainer ref={mapContainerRef} id={id}>
        {children}
      </MapContainer>
      <MarkerPin visible={pinData} ref={ideaPinRef}>
        <IdeaPin transform={"translateY(-13px)"} color={ideaMarkerColor} />
      </MarkerPin>

      <div
        className={cn(
          "absolute  flex  flex-col gap-2",
          isMobile && variant === "ideaMap"
            ? "right-2 top-[calc(100svh_-_240px)]"
            : isMobile
              ? "left-4 top-4"
              : "bottom-[134px] right-2.5",
        )}
      >
        {legend && legend?.length ? (
          <Dropdown
            variant="outline"
            width="max"
            size="md"
            className="rounded-full bg-white shadow-xl"
            trailingIcon="paragraph"
            items={legend
              ?.sort((a, b) => {
                if (a?.title < b?.title) {
                  return -1; // a comes before b
                }
                if (a?.title > b?.title) {
                  return 1; // b comes before a
                }
                return 0; // a and b are equivalent
              })
              .map((legendItem) => ({
                text: legendItem.title,
                leadingIcon: <Dot color={legendItem.color} />,
              }))}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MapboxMap;
